import { type Color, type WebGLRenderer, type Object3D } from 'three';
import type { AudioChannel, AudioManager } from '../audio/audioManager';
import type { SunRig } from '../atmosphere/sunRig';
import type { Atmosphere } from '../atmosphere/atmosphere';
import type { CameraMode } from '../camera/cameraMode';
import type { ClothPatch } from '../scene/clothPatch';
import type { AudioAsset } from '../audio/manifest';
import type { DebugVizScene, GizmoCategory } from './dvScene';
import { devBus, focusMatches, type FocusRef } from './devBus';
import { channelCssColor, applyFocusGlow } from './audioChannelColors';
import { onDevModeChange, setDevMode } from './devMode';
import { hotkeyRegistry } from './hotkeyRegistry';
import { isTextEntryTarget } from './keyboardTarget';
import { TIMELINE_HEIGHT } from './timeline';
import { type SaveVis, paintSave } from './saveVis';
import type { SaveResult } from '../persist/saveFlows';

// Inspector panel — left-edge collapsible dev surface with two tabs in
// Phase 4 (Heroes + Audio). Atmosphere + Perf tabs land in Phase 8.
//
// Layout (280px wide, full height minus header/timeline padding):
//
//   ┌──────────────────────┐
//   │ inspector       _  × │  ← header with collapse + close
//   ├──────────────────────┤
//   │ HEROES  AUDIO  · ·   │  ← tab bar
//   ├──────────────────────┤
//   │  hero_speaker  ◆ ·   │  ← rows: name + interactive chip + click-target
//   │  hero_boombox  ◆ ·   │
//   │  ...                 │
//   └──────────────────────┘
//
// HEROES tab — one row per hero in the dropdown map. Row click sets the
// gizmo edit target (same path as the lil-gui dropdown). Cyan diamond
// chip for interactive heroes; gray for set pieces.
//
// AUDIO tab — master volume + muted toggle, 4 channel volume sliders, and
// a live list of currently playing entries with per-entry volume + stop.
// The list rebuilds on audio:play / audio:stop events.

const PANEL_BG = 'rgba(18, 22, 28, 0.86)';
const HOVER_BG = 'rgba(255,255,255,0.04)';
const SELECTED_BG = 'rgba(102, 255, 230, 0.10)';
const ACCENT = '#66ffe6';
const MUTED = '#6c7480';
const TEXT = '#e6ebef';
const SET_CHIP = '#7a7f88';
const SUBHEAD = '#9aa3ad';

const LS_COLLAPSED = 'lec_dev_inspector_collapsed';
const LS_TAB = 'lec_dev_inspector_tab';
const LS_PALETTE = 'lec_dev_inspector_palette';

type Tab = 'heroes' | 'audio' | 'atmosphere' | 'camera' | 'perf';

export interface InspectorOpts {
  audio: AudioManager;
  /** Handles the bed reconciler owns right now (U8). Rows whose handles are
   *  ALL engine-owned hide stop/volume (stopping is futile — the reconciler
   *  blip-restarts with a re-rolled variant; gain is rewritten per frame);
   *  mixed rows scope their controls to the non-engine handles only, so a
   *  same-asset audition's stop can't kill the live bed. */
  bedOwnedHandles?: () => Set<string>;
  heroLookup: Map<string, Object3D>;
  heroIds: Record<string, string>; // label → heroId, from buildHeroDropdownMap
  setEditTarget: (id: string) => void;
  /** Read the currently edited target so the inspector can highlight it. */
  getEditTarget: () => string;
  /** Called after a hero's state tag changes (past/present/both) so the
   *  caller can re-init the transition + refresh dropdown labels. */
  onHeroRetag?: () => void;
  /** DM4 — record an undoable command on the shared gizmo-undo stack (used by
   *  the hero retag). Wired to gizmoUndo.pushCommand in app.ts. */
  pushUndo?: (undo: () => void, redo: () => void) => void;
  // Phase 8: atmosphere + perf
  sunRig: SunRig;
  atmospheres: Record<string, Atmosphere>;
  getActiveAtmosphere: () => Atmosphere;
  setAtmosphere: (name: string) => void;
  renderer: WebGLRenderer;
  cloths: ClothPatch[];
  /** 3D debug-viz scene — the inspector's gizmo footer drives it. */
  dv: DebugVizScene;
  // ── audio palette (audition + arm) ──
  // Hero beds are no longer authored here — they live on the timeline's
  // AMBIENCE lane, and the SoundscapeEngine owns bed playback (a manual mixer
  // here would double-trigger against it).
  /** Full audio catalogue for the quick-play (audition) chip grid. */
  audioAssets: AudioAsset[];
  // ── atmosphere shaft editing (absorbed from the lil-gui atmosphere folder) ──
  /** Shaft-handle ids for the ATMOSPHERE tab's handle picker ('(none)' + N). */
  shaftHandleIds: string[];
  /** Point the gizmo at a shaft handle (or '(none)' to detach). */
  setShaftEditTarget: (id: string) => void;
  /** Persist shaft endpoints/radii/tunables → morning-shaft.json. Resolves
   *  true on success / false on failure so the button shows an honest ✓/✕ (S5). */
  saveShaftConfig: () => Promise<boolean>;
  /** Jump time-of-day to the shaft's reference t (a shaft-authoring aid). */
  snapToShaftRef: () => void;
  // ── hero editing (absorbed from the lil-gui edit folder) ──
  /** Transform-gizmo mode for the HEROES-tab toggle (W/E also switch it). */
  getGizmoMode: () => 'translate' | 'rotate' | 'scale';
  setGizmoMode: (mode: 'translate' | 'rotate' | 'scale') => void;
  /** Set a hero's local scale (uniform or per-axis); records undo + persists on ⌘S. */
  setHeroScale: (id: string, scale: { x: number; y: number; z: number }) => void;
  /** Persist edited hero positions → manifest.json. Resolves true/false (S5). */
  saveHeroPositions: () => Promise<SaveResult>; // DM5 — conflict-aware
  // ── camera (absorbed from the lil-gui camera folder) ──
  cameras: Record<string, CameraMode>;
  getActiveCamera: () => CameraMode;
  setCamera: (name: string) => void;
  cullSettings: { offset: number; enabled: boolean };
  bookmarks: Record<string, { position: [number, number, number]; target: [number, number, number]; t?: number; anchor?: string }>;
  goToBookmark: (name: string) => void;
  // All resolve true on success / false on failure for honest ✓/✕ feedback (S5).
  saveCurrentAsBookmark: (name: string) => Promise<boolean>;
  /** DM13 — save the current pose anchored to a hero (offered on selection). */
  saveHeroAnchoredBookmark: (heroId: string, name: string) => Promise<boolean>;
  deleteBookmark: (name: string) => Promise<boolean>;
  saveCameraPose: () => Promise<boolean>;
  saveCurrentAsDoorway: () => Promise<boolean>;
}

export function createInspector(opts: InspectorOpts): {
  root: HTMLElement;
  refreshHeroes(newMap: Record<string, string>): void;
  dispose(): void;
} {
  const root = document.createElement('div');
  root.id = 'lec-inspector';
  Object.assign(root.style, {
    position: 'fixed',
    // Flush to the top-right corner (the dev view leaves it free). Sizes to its
    // content and only begins to scroll once that content would reach the
    // timeline strip — the cap stops 12px above it. (The event log lives at the
    // bottom-left, so the inspector owns the full right column.)
    top: '12px',
    right: '12px',
    width: '300px',
    // Clear of the timeline strip's LIVE height (it auto-sizes to its content);
    // falls back to the static reserve before the strip's first layout.
    maxHeight: `calc(100vh - (var(--lec-timeline-h, ${TIMELINE_HEIGHT}px) + 24px))`,
    zIndex: '35',
    background: PANEL_BG,
    color: TEXT,
    font: '11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    userSelect: 'none',
  });

  // ── header (collapse / close) ──────────────────────────────────────
  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
  });
  const headerLabel = document.createElement('span');
  headerLabel.textContent = 'inspector';
  headerLabel.style.fontWeight = '600';
  // Right side of the header: a director-mode toggle + the collapse caret.
  // The director button is the discoverable way to drop into the clean
  // viewer view (the D hotkey does the same). stopPropagation keeps a
  // click on it from also toggling collapse.
  const headerRight = document.createElement('div');
  Object.assign(headerRight.style, { display: 'flex', alignItems: 'center', gap: '10px' });
  const directorBtn = document.createElement('span');
  directorBtn.textContent = 'director';
  directorBtn.title = 'hide all dev UI (D)';
  Object.assign(directorBtn.style, {
    color: MUTED,
    fontSize: '9px',
    letterSpacing: '0.5px',
    padding: '1px 6px',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    cursor: 'pointer',
  });
  directorBtn.addEventListener('mouseenter', () => { directorBtn.style.borderColor = ACCENT; directorBtn.style.color = ACCENT; });
  directorBtn.addEventListener('mouseleave', () => { directorBtn.style.borderColor = 'rgba(255,255,255,0.12)'; directorBtn.style.color = MUTED; });
  directorBtn.addEventListener('click', (e) => { e.stopPropagation(); setDevMode(false); });
  // Exit-dev — the affordance the dev view was missing (you previously had to
  // hand-edit the URL). Drops ?dev and reloads back to the viewer. Distinct
  // from `director`, which only hides the HUD while staying in dev.
  const exitBtn = document.createElement('span');
  exitBtn.textContent = '✕';
  exitBtn.title = 'exit dev view (back to viewer)';
  Object.assign(exitBtn.style, { color: MUTED, fontSize: '12px', lineHeight: '1', cursor: 'pointer' });
  exitBtn.addEventListener('mouseenter', () => { exitBtn.style.color = '#ff8a8a'; });
  exitBtn.addEventListener('mouseleave', () => { exitBtn.style.color = MUTED; });
  exitBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const url = new URL(window.location.href);
    url.searchParams.delete('dev');
    window.location.href = url.toString();
  });
  // Help — surfaces the keyboard cheatsheet (it always listened on '?', but had
  // no visible affordance). Click dispatches the same '?' the modal listens for.
  const helpBtn = document.createElement('span');
  helpBtn.textContent = '?';
  helpBtn.title = 'keyboard shortcuts (?)';
  Object.assign(helpBtn.style, { color: MUTED, fontSize: '11px', fontWeight: '600', cursor: 'pointer' });
  helpBtn.addEventListener('mouseenter', () => { helpBtn.style.color = ACCENT; });
  helpBtn.addEventListener('mouseleave', () => { helpBtn.style.color = MUTED; });
  helpBtn.addEventListener('click', (e) => { e.stopPropagation(); window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' })); });
  const collapseToggle = document.createElement('span');
  collapseToggle.title = 'collapse / expand the panel';
  collapseToggle.style.color = MUTED;
  collapseToggle.style.cursor = 'pointer';
  headerRight.appendChild(helpBtn);
  headerRight.appendChild(directorBtn);
  headerRight.appendChild(exitBtn);
  headerRight.appendChild(collapseToggle);
  header.appendChild(headerLabel);
  header.appendChild(headerRight);
  root.appendChild(header);

  // ── tab bar ────────────────────────────────────────────────────────
  const tabBar = document.createElement('div');
  Object.assign(tabBar.style, {
    display: 'flex',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  });
  const tabBtns: Record<Tab, HTMLDivElement> = {} as never;
  for (const t of ['heroes', 'audio', 'atmosphere', 'camera', 'perf'] as Tab[]) {
    const b = document.createElement('div');
    b.textContent = t.toUpperCase();
    Object.assign(b.style, {
      flex: '1 1 0',
      padding: '6px 3px',
      cursor: 'pointer',
      fontSize: '9px',
      letterSpacing: '0',
      color: MUTED,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      borderBottom: '2px solid transparent',
    });
    b.addEventListener('click', () => setTab(t));
    tabBar.appendChild(b);
    tabBtns[t] = b;
  }
  root.appendChild(tabBar);

  // ── tab content ────────────────────────────────────────────────────
  const content = document.createElement('div');
  Object.assign(content.style, {
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: '8px 10px',
  });
  root.appendChild(content);

  // ── gizmo footer: master show-all / hide-all only ───────────────────
  // The per-category toggles moved next to the data they visualize
  // (audio→AUDIO tab, lighting→ATMOSPHERE, cloth/culling/cameras→PERF) via
  // mkGizmoToggle. This footer keeps just the master — the common
  // "show/hide everything" — which the G hotkey also drives.
  const gizmoFooter = document.createElement('div');
  Object.assign(gizmoFooter.style, {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: '0',
  });
  const gizmoMaster = document.createElement('span');
  gizmoMaster.style.cursor = 'pointer';
  gizmoMaster.style.fontSize = '10px';
  gizmoMaster.style.fontWeight = '600';
  gizmoMaster.title = 'show / hide all gizmos (G)';
  gizmoMaster.addEventListener('click', () => opts.dv.toggle());
  gizmoFooter.appendChild(gizmoMaster);
  const gizmoHint = document.createElement('span');
  gizmoHint.textContent = 'per-layer toggles in tabs';
  Object.assign(gizmoHint.style, { fontSize: '9px', color: MUTED, marginLeft: 'auto' });
  gizmoFooter.appendChild(gizmoHint);
  root.appendChild(gizmoFooter);
  const unsubGizmo = opts.dv.onChange((state) => {
    gizmoMaster.textContent = state.enabled ? '◉ gizmos' : '○ gizmos';
    gizmoMaster.style.color = state.enabled ? ACCENT : MUTED;
  });
  const onGizmoKey = (e: KeyboardEvent): void => {
    if (isTextEntryTarget(e)) return; // U2: also bail on focused <select>/contentEditable
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'g' || e.key === 'G') { e.preventDefault(); opts.dv.toggle(); }
  };
  window.addEventListener('keydown', onGizmoKey);
  const unregHotkey = hotkeyRegistry.register({
    keys: 'G', description: 'show / hide all gizmos', section: 'dev HUD', order: 0,
  });

  // ── state ──────────────────────────────────────────────────────────
  let currentTab: Tab = (localStorage.getItem(LS_TAB) as Tab) || 'heroes';
  let collapsed = localStorage.getItem(LS_COLLAPSED) === '1';
  // The sound palette is the tall sound library — collapsed by default so the
  // AUDIO tab fits without scrolling; one click expands it to browse. Persisted.
  let paletteOpen = localStorage.getItem(LS_PALETTE) === '1';
  let heroIdsMap = { ...opts.heroIds };
  // Unified selection — driven by devBus hero:select / hero:hover. `selected`
  // is the active hero echoed everywhere (row + AABB + bed + 3D outline);
  // `hovered` is the transient 3D-hover echo. Both restyle rows in place.
  let selectedHeroId: string | null = opts.getEditTarget() === '(none)' ? null : opts.getEditTarget();
  let hoveredHeroId: string | null = null;
  // The sound "armed" in the palette as the CUES-drop brush — kept in sync
  // with the timeline's picker via the audio:arm bus event.
  let armedAssetId: string | null = null;
  const heroRowEls = new Map<string, { row: HTMLDivElement; name: HTMLSpanElement }>();
  // Cross-surface audio focus (hover-to-illuminate). A hover on any audio
  // surface lights matching elements here — palette chip by asset, PLAYING-NOW
  // row by asset, channel fader by channel, hero row by anchored hero — in the
  // sound's channel color; our own hovers re-broadcast it on the bus. Focusable
  // chips/rows stash their channel in a `_fch` property for the paint color.
  let currentFocus: FocusRef | null = null;
  const channelFaderRows = new Map<AudioChannel, HTMLElement>();
  const emitFocus = (ref: FocusRef | null): void => devBus.emit('audio:focus', { ref });
  // Active morning-shaft handle in the ATMOSPHERE tab (mirrors the gizmo
  // target). '(none)' = nothing being edited.
  let activeShaftHandle = '(none)';
  // HEROES-tab gizmo-mode toggle buttons (translate/rotate), kept in sync
  // with the W/E keys via a light poll.
  let gizmoModeBtns: Partial<Record<'translate' | 'rotate' | 'scale', HTMLSpanElement>> = {};
  // HEROES-tab numeric scale inputs (uniform + per-axis), synced to the selected
  // hero. Null until the heroes tab is rendered. (B5)
  let scaleInputs: { uni: HTMLInputElement; x: HTMLInputElement; y: HTMLInputElement; z: HTMLInputElement } | null = null;
  // DM13 — the "frame this hero" offer (a hero-anchored bookmark), synced to the
  // selected hero. Affordance-specific labels give the gate's "sit here" feel.
  let frameOfferBtn: HTMLButtonElement | null = null;
  const FRAME_LABELS: Record<string, string> = {
    hero_chair: 'sit here', hero_counter: 'at the counter', hero_deck: 'over the deck',
    hero_boombox: 'at the boombox', hero_sewing_machine: 'at the machine',
  };
  const affordanceLabel = (id: string): string => FRAME_LABELS[id] ?? `frame ${id.replace(/^hero_/, '')}`;
  // dv.onChange unsubs for the per-tab gizmo toggles — cleared on tab switch
  // (and dispose) so re-rendering a tab can't leak listeners.
  const tabGizmoUnsubs: Array<() => void> = [];

  function setTab(t: Tab): void {
    currentTab = t;
    try { localStorage.setItem(LS_TAB, t); } catch { /* ignored */ }
    for (const [n, b] of Object.entries(tabBtns) as [Tab, HTMLDivElement][]) {
      if (n === t) {
        b.style.color = ACCENT;
        b.style.borderBottomColor = ACCENT;
      } else {
        b.style.color = MUTED;
        b.style.borderBottomColor = 'transparent';
      }
    }
    renderTab();
  }

  function setCollapsed(v: boolean): void {
    collapsed = v;
    try { localStorage.setItem(LS_COLLAPSED, v ? '1' : '0'); } catch { /* ignored */ }
    collapseToggle.textContent = collapsed ? '+' : '–';
    tabBar.style.display = collapsed ? 'none' : 'flex';
    content.style.display = collapsed ? 'none' : 'block';
  }
  header.addEventListener('click', () => setCollapsed(!collapsed));

  function refreshHeroes(newMap: Record<string, string>): void {
    heroIdsMap = { ...newMap };
    if (currentTab === 'heroes') renderTab();
  }

  // ── render: heroes tab ─────────────────────────────────────────────
  // Row = name (click → edit/gizmo) · interactive chip · state segment
  // (past/pres/both). The state segment is the absorbed hero-state panel:
  // it writes obj.userData.state and fires onHeroRetag so the transition
  // re-inits + the dropdowns refresh. Group (★) / #all bulk handles have
  // no state, so they show no segment.
  function renderHeroes(): void {
    content.innerHTML = '';
    heroRowEls.clear();
    const hint = document.createElement('div');
    hint.textContent = 'name = edit · pa/pr/bo = state tag';
    Object.assign(hint.style, { color: MUTED, fontSize: '9px', marginBottom: '8px' });
    content.appendChild(hint);

    // Edit controls (absorbed from the lil-gui edit folder): gizmo mode + save.
    gizmoModeBtns = {};
    const editRow = document.createElement('div');
    Object.assign(editRow.style, { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' });
    const modeWrap = document.createElement('div');
    Object.assign(modeWrap.style, { display: 'flex', gap: '2px' });
    for (const [mode, lab] of [['translate', 'move (W)'], ['rotate', 'rotate (E)'], ['scale', 'scale (R)']] as Array<['translate' | 'rotate' | 'scale', string]>) {
      const b = document.createElement('span');
      b.textContent = lab;
      b.title = `gizmo ${mode}`;
      Object.assign(b.style, { fontSize: '9px', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.12)', color: MUTED });
      b.addEventListener('click', () => { opts.setGizmoMode(mode); syncGizmoMode(); });
      modeWrap.appendChild(b);
      gizmoModeBtns[mode] = b;
    }
    editRow.appendChild(modeWrap);
    const saveManifestBtn = mkButton('save → manifest.json', () => {
      flashSaveResult(saveManifestBtn, 'save → manifest.json', opts.saveHeroPositions());
    });
    editRow.appendChild(saveManifestBtn);
    // DM13 — the contextual "frame this hero" offer: with a hero selected, save a
    // hero-ANCHORED bookmark (a fitting viewpoint that still frames the hero after
    // it moves). The label is affordance-specific (chair → "sit here").
    const frameBtn = mkButton('frame ⤓', () => {
      if (!selectedHeroId) return;
      const suggested = affordanceLabel(selectedHeroId);
      const name = window.prompt('Bookmark name (frames this hero)?', suggested);
      if (!name || !name.trim()) return;
      flashSaveResult(frameBtn, frameBtn.textContent || 'frame ⤓', opts.saveHeroAnchoredBookmark(selectedHeroId, name.trim()));
    });
    frameOfferBtn = frameBtn;
    editRow.appendChild(frameBtn);
    content.appendChild(editRow);

    // B5 — numeric scale entry for the SELECTED hero (uniform + per-axis).
    // Mirrors the gizmo's scale mode; both record undo + persist on ⌘S. Empty +
    // disabled until a hero is selected (the gizmo target).
    const scaleRow = document.createElement('div');
    Object.assign(scaleRow.style, { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px', fontSize: '9px', color: MUTED });
    const sLbl = document.createElement('span');
    sLbl.textContent = 'scale';
    scaleRow.appendChild(sLbl);
    const mkScaleInput = (ph: string, title: string): HTMLInputElement => {
      const inp = document.createElement('input');
      inp.type = 'number'; inp.step = '0.05'; inp.min = '0.01'; inp.max = '100';
      inp.placeholder = ph; inp.title = title;
      Object.assign(inp.style, { width: '42px', fontSize: '9px', padding: '1px 3px', background: 'rgba(255,255,255,0.06)', color: TEXT, border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px' });
      return inp;
    };
    const uniInp = mkScaleInput('all', 'uniform scale (sets x = y = z)');
    const sxInp = mkScaleInput('x', 'scale X');
    const syInp = mkScaleInput('y', 'scale Y');
    const szInp = mkScaleInput('z', 'scale Z');
    scaleInputs = { uni: uniInp, x: sxInp, y: syInp, z: szInp };
    const applyAxes = (): void => {
      if (!selectedHeroId) return;
      opts.setHeroScale(selectedHeroId, { x: parseFloat(sxInp.value), y: parseFloat(syInp.value), z: parseFloat(szInp.value) });
      syncScaleFields();
    };
    sxInp.addEventListener('change', applyAxes);
    syInp.addEventListener('change', applyAxes);
    szInp.addEventListener('change', applyAxes);
    uniInp.addEventListener('change', () => {
      if (!selectedHeroId) return;
      const v = parseFloat(uniInp.value);
      if (!Number.isFinite(v)) return;
      opts.setHeroScale(selectedHeroId, { x: v, y: v, z: v });
      syncScaleFields();
    });
    scaleRow.append(uniInp, sxInp, syInp, szInp);
    content.appendChild(scaleRow);

    syncGizmoMode();
    syncScaleFields();
    syncFrameOffer(); // DM13

    const rows = Object.entries(heroIdsMap);
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = '(no heroes loaded)';
      empty.style.color = MUTED;
      content.appendChild(empty);
      return;
    }
    for (const [label, id] of rows) {
      if (id === '(none)') continue;
      const obj = opts.heroLookup.get(id);
      const interactive = obj?.userData?.interactive !== false;
      const isBulk = id.startsWith('group:') || id.endsWith('#all');
      const row = mkHeroRow({
        // Real heroes show the bare id (the state segment replaces the
        // "(past)" label suffix); bulk handles keep their decorated label.
        name: isBulk ? label : id,
        id, interactive, selected: id === selectedHeroId,
        obj: obj ?? null,
        showState: !isBulk && !!obj,
      });
      content.appendChild(row);
    }
    applyHeroRowStates();
  }

  // Restyle hero rows in place from the unified selection/hover state — no
  // full re-render, so scroll position and focus survive a 3D click/hover.
  function applyHeroRowStates(): void {
    if (currentTab !== 'heroes') return;
    for (const [id, refs] of heroRowEls) {
      const isSel = id === selectedHeroId;
      const isHov = !isSel && id === hoveredHeroId;
      refs.row.style.background = isSel ? SELECTED_BG : isHov ? HOVER_BG : 'transparent';
      refs.name.style.color = isSel ? ACCENT : TEXT;
    }
    syncScaleFields(); // B5 — follow the selection into the scale inputs
    syncFrameOffer();  // DM13 — follow the selection into the frame offer
  }

  // Highlight the active transform-gizmo mode on the HEROES-tab toggle.
  function syncGizmoMode(): void {
    const m = opts.getGizmoMode();
    for (const k of ['translate', 'rotate', 'scale'] as const) {
      const b = gizmoModeBtns[k];
      if (!b) continue;
      const active = k === m;
      b.style.background = active ? ACCENT : 'transparent';
      b.style.color = active ? '#0e1218' : MUTED;
      b.style.borderColor = active ? ACCENT : 'rgba(255,255,255,0.12)';
    }
  }

  // Fill the HEROES-tab scale inputs from the selected hero (the gizmo target),
  // or disable them when nothing's selected. Called on render + on selection
  // change so a 3D / inspector pick updates the fields without a full re-render.
  // Skips writing an input the user is actively typing in, so a stray hover
  // re-sync can't clobber an in-progress edit. (B5)
  function syncScaleFields(): void {
    if (currentTab !== 'heroes' || !scaleInputs) return;
    const ae = document.activeElement;
    if (ae === scaleInputs.uni || ae === scaleInputs.x || ae === scaleInputs.y || ae === scaleInputs.z) return;
    const obj = selectedHeroId ? opts.heroLookup.get(selectedHeroId) : null;
    const on = !!obj;
    for (const k of ['uni', 'x', 'y', 'z'] as const) {
      scaleInputs[k].disabled = !on;
      scaleInputs[k].style.opacity = on ? '1' : '0.4';
    }
    if (!obj) { (['uni', 'x', 'y', 'z'] as const).forEach((k) => { scaleInputs![k].value = ''; }); return; }
    const s = obj.scale;
    const f = (n: number): string => String(+n.toFixed(3));
    scaleInputs.x.value = f(s.x);
    scaleInputs.y.value = f(s.y);
    scaleInputs.z.value = f(s.z);
    scaleInputs.uni.value = (s.x === s.y && s.y === s.z) ? f(s.x) : '';
  }

  // DM13 — the "frame this hero" offer follows the selection: enabled + labelled
  // with the selected hero's affordance ("sit here"), disabled with no selection.
  function syncFrameOffer(): void {
    if (currentTab !== 'heroes' || !frameOfferBtn) return;
    const id = selectedHeroId;
    const real = !!id && id !== '(none)' && !!opts.heroLookup.get(id);
    frameOfferBtn.disabled = !real;
    frameOfferBtn.style.opacity = real ? '1' : '0.4';
    frameOfferBtn.textContent = real ? `${affordanceLabel(id!)} ⤓` : 'frame ⤓';
    frameOfferBtn.title = real ? `save a bookmark that frames ${id} — stays framed after the hero moves (DM13)` : 'select a hero to frame it';
  }

  function mkHeroRow(p: {
    name: string; id: string; interactive: boolean; selected: boolean;
    obj: Object3D | null; showState: boolean;
  }): HTMLDivElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 6px',
      borderRadius: '4px',
      background: p.selected ? SELECTED_BG : 'transparent',
    });
    const name = document.createElement('span');
    name.textContent = p.name;
    name.style.color = p.selected ? ACCENT : TEXT;
    name.style.whiteSpace = 'nowrap';
    name.style.overflow = 'hidden';
    name.style.textOverflow = 'ellipsis';
    name.style.flex = '1 1 auto';
    name.style.cursor = 'pointer';
    name.title = 'edit / move (gizmo follows)';
    name.addEventListener('mouseenter', () => { if (p.id !== selectedHeroId) name.style.color = ACCENT; });
    name.addEventListener('mouseleave', () => { name.style.color = p.id === selectedHeroId ? ACCENT : TEXT; });
    // Row click sets the edit target (gizmo + camera); setEditTarget then
    // emits hero:select, which restyles the row via applyHeroRowStates — no
    // explicit re-render needed here.
    name.addEventListener('click', () => { opts.setEditTarget(p.id); });
    const chip = document.createElement('span');
    chip.textContent = p.interactive ? '◆' : '◇';
    chip.style.color = p.interactive ? ACCENT : SET_CHIP;
    chip.style.fontSize = '10px';
    chip.style.flexShrink = '0';
    chip.title = p.interactive ? 'interactive' : 'set';
    row.appendChild(name);
    row.appendChild(chip);
    if (p.showState && p.obj) row.appendChild(mkStateSeg(p.obj));
    heroRowEls.set(p.id, { row, name });
    return row;
  }

  // Three-segment state tag control (past / present / both). Writes
  // obj.userData.state and fires onHeroRetag (re-init transition + refresh
  // dropdowns). Click is stopPropagation'd so it never doubles as a select.
  function mkStateSeg(obj: Object3D): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { display: 'flex', gap: '2px', flexShrink: '0' });
    const cur = (obj.userData.state ?? 'past') as string;
    const defs: Array<[string, string]> = [['past', 'pa'], ['present', 'pr'], ['both', 'bo']];
    for (const [val, lab] of defs) {
      const b = document.createElement('span');
      b.textContent = lab;
      b.title = val;
      const active = cur === val;
      Object.assign(b.style, {
        fontSize: '9px',
        padding: '1px 5px',
        borderRadius: '3px',
        cursor: 'pointer',
        color: active ? '#0e1218' : MUTED,
        background: active ? ACCENT : 'transparent',
        border: `1px solid ${active ? ACCENT : 'rgba(255,255,255,0.12)'}`,
      });
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const before = (obj.userData.state ?? 'past') as string;
        if (before === val) return; // already this tag — no edit, no undo entry
        const setState = (s: string): void => {
          obj.userData.state = s;
          opts.onHeroRetag?.();
          renderTab();
          applyHeroRowStates(); // DM4 — keep the selection highlight through the rebuild
        };
        setState(val);
        // DM4 — retag is undoable: push a command onto the shared gizmo-undo
        // stack (one recency-arbitrated timeline, so ⌘Z reverts the last edit
        // whatever surface it was on — toward DM8). ⌘Z → before; ⌘⇧Z → val.
        opts.pushUndo?.(() => setState(before), () => setState(val));
      });
      wrap.appendChild(b);
    }
    return wrap;
  }

  // ── render: audio tab ──────────────────────────────────────────────
  // Holds refs to per-entry rows for incremental update on volume drag.
  const audioRows = new Map<string, { row: HTMLDivElement; slider: HTMLInputElement }>();
  // Dedicated container for the PLAYING-NOW rows so the audio poll's re-renders
  // keep them in place (under the subhead) instead of re-appending to the end of
  // `content` — which would shove them below the sound palette.
  let playingBox: HTMLDivElement | null = null;
  // Refs to the OUTPUT mute + master so the audio poll can keep them in sync
  // with the chrome mute (the two controls drive the same state).
  let outMaster: { set: (v: number) => void } | null = null;
  let outMuted: { set: (v: boolean) => void } | null = null;

  function renderAudio(): void {
    content.innerHTML = '';
    audioRows.clear();
    channelFaderRows.clear();
    content.appendChild(mkGizmoToggle('audio', 'audio gizmos'));

    // Master section
    const master = mkSubhead('output');
    content.appendChild(master);
    const muted = mkCheckbox('muted', opts.audio.muted, (v) => opts.audio.setMuted(v));
    content.appendChild(muted.row);
    outMuted = muted;
    const masterVol = mkSlider('master', opts.audio.getMasterVolume(), (v) => opts.audio.setMasterVolume(v));
    content.appendChild(masterVol.row);
    outMaster = masterVol;

    // Channels section
    const chans = mkSubhead('channels');
    chans.style.marginTop = '8px';
    content.appendChild(chans);
    for (const ch of ['ambient', 'music', 'narration', 'sfx', 'room'] as AudioChannel[]) {
      const v = opts.audio.getChannelVolume(ch);
      const s = mkSlider(ch, v, (newV) => opts.audio.setChannelVolume(ch, newV));
      // The fader is the channel's home in the mix — hovering it lights every
      // sound on that channel (lanes, chips, 3D spheres); a focus carrying only
      // that channel lights the fader back.
      const fcand: FocusRef = { channel: ch };
      s.row.addEventListener('mouseenter', () => emitFocus(fcand));
      s.row.addEventListener('mouseleave', () => emitFocus(null));
      channelFaderRows.set(ch, s.row);
      content.appendChild(s.row);
    }

    // Playing now section — the per-instance MIXER (volume + stop). Its
    // counterpart is the timeline SOUND lane (the at-a-glance live readout);
    // the two are cross-linked by focus, so this caption names the relationship
    // rather than letting them read as redundant.
    const playing = mkSubhead('playing now');
    playing.style.marginTop = '8px';
    playing.title = 'live mix — hover a row to find it on the timeline + in 3D';
    content.appendChild(playing);
    playingBox = document.createElement('div');
    content.appendChild(playingBox);
    renderPlayingList();

    // Beds author on the timeline's AMBIENCE lane; the collapsible sound palette
    // below is the library (audition + arm), so there's no separate beds section.
    renderAssets();
  }

  // ── sound palette (browse + audition + arm) ─────────────────────────
  // The library half of "place a sound in time": grouped by source, each
  // chip colored by its channel (shared color language) and marked ∞ if it
  // loops. Clicking a chip auditions it AND arms it as the CUES-drop brush
  // (via audio:arm) — then click the CUES lane at a time to place it.
  const assetChips = new Map<string, HTMLSpanElement>();
  function renderAssets(): void {
    assetChips.clear();
    // Collapsible header — the palette is the tall sound library, so it's
    // collapsed by default to keep the AUDIO tab scroll-free; a caret + count
    // make the affordance obvious, and the place-it hint lives in the tooltip.
    const head = document.createElement('div');
    Object.assign(head.style, { display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: SUBHEAD, fontSize: '9px', letterSpacing: '0.5px', textTransform: 'uppercase', marginTop: '8px', marginBottom: '4px', userSelect: 'none' });
    head.title = 'click a chip to hear + arm → then click the CUES lane to place';
    const caret = document.createElement('span'); caret.style.fontSize = '8px';
    const headLbl = document.createElement('span'); headLbl.textContent = 'sound palette';
    const count = document.createElement('span'); count.textContent = String(opts.audioAssets.length); count.style.opacity = '0.5';
    head.append(caret, headLbl, count);
    content.appendChild(head);
    const box = document.createElement('div');
    content.appendChild(box);
    const syncPalette = (): void => { caret.textContent = paletteOpen ? '▾' : '▸'; box.style.display = paletteOpen ? 'block' : 'none'; };
    head.addEventListener('click', () => {
      paletteOpen = !paletteOpen;
      try { localStorage.setItem(LS_PALETTE, paletteOpen ? '1' : '0'); } catch { /* ignored */ }
      syncPalette();
    });
    syncPalette();
    const grouped = new Map<string, AudioAsset[]>();
    for (const a of opts.audioAssets) {
      const g = PALETTE_GROUPS.find((G) => G.match(a.id))?.name ?? 'Other';
      const arr = grouped.get(g) ?? [];
      arr.push(a);
      grouped.set(g, arr);
    }
    for (const gName of [...PALETTE_GROUPS.map((g) => g.name), 'Other']) {
      const assets = grouped.get(gName);
      if (!assets || assets.length === 0) continue;
      const gh = document.createElement('div');
      gh.textContent = gName;
      Object.assign(gh.style, { color: MUTED, fontSize: '8px', letterSpacing: '0.5px', textTransform: 'uppercase', marginTop: '4px', marginBottom: '2px', opacity: '0.7' });
      box.appendChild(gh);
      const grid = document.createElement('div');
      Object.assign(grid.style, { display: 'flex', flexWrap: 'wrap', gap: '4px' });
      for (const a of assets) {
        const color = channelCssColor(a.defaultChannel ?? 'sfx');
        const chip = document.createElement('span');
        Object.assign(chip.style, {
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontSize: '9px', padding: '2px 6px', borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', color: TEXT, whiteSpace: 'nowrap',
        });
        const dot = document.createElement('span');
        Object.assign(dot.style, { width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: '0' });
        const label = document.createElement('span');
        label.textContent = stripPrefix(a.id) + (a.loopable ? ' ∞' : '');
        chip.appendChild(dot);
        chip.appendChild(label);
        chip.title = `${a.id} · ${a.defaultChannel ?? 'sfx'}${a.loopable ? ' · loops' : ' · one-shot'} · click to hear + arm`;
        chip.addEventListener('click', async () => {
          try { await opts.audio.resume(); } catch { /* ignored */ }
          opts.audio.play(a.id, { channel: 'sfx', volume: 0.8, fadeIn: 0.05, exclusive: true });
          devBus.emit('audio:arm', { id: a.id });
        });
        // Cross-surface focus: hovering the library chip lights every live
        // placement / instance of this asset (lanes, SOUND chips, 3D spheres);
        // _fch carries the channel so the glow paints in the right color.
        const pch = a.defaultChannel ?? 'sfx';
        (chip as HTMLSpanElement & { _fch?: string })._fch = pch;
        const pcand: FocusRef = { assetId: a.id, channel: pch };
        chip.addEventListener('mouseenter', () => emitFocus(pcand));
        chip.addEventListener('mouseleave', () => emitFocus(null));
        assetChips.set(a.id, chip);
        grid.appendChild(chip);
      }
      box.appendChild(grid);
    }
    refreshAssets();
    refreshArmed();
  }
  function refreshAssets(): void {
    const playingIds = new Set(opts.audio.listPlaying().map((p) => p.id));
    for (const [id, chip] of assetChips) {
      chip.style.color = playingIds.has(id) ? ACCENT : TEXT;
    }
  }
  function refreshArmed(): void {
    for (const [id, chip] of assetChips) {
      const on = id === armedAssetId;
      chip.style.borderColor = on ? ACCENT : 'rgba(255,255,255,0.15)';
      chip.style.background = on ? 'rgba(102,255,230,0.12)' : 'transparent';
    }
  }

  function renderPlayingList(): void {
    // Wipe any prior playing-list rows. The first three groups (output,
    // channels) stay; we only rebuild the bottom.
    for (const { row } of audioRows.values()) row.remove();
    audioRows.clear();
    const entries = opts.audio.listPlaying();
    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = '(nothing playing)';
      empty.style.color = MUTED;
      empty.style.fontSize = '10px';
      empty.dataset.placeholder = '1';
      (playingBox ?? content).appendChild(empty);
      audioRows.set('__placeholder__', { row: empty, slider: null as never });
      return;
    }
    // Coalesce by id|channel — ONE row per distinct sound. The same asset
    // sounding from several places, or briefly doubled mid-era-crossfade, is a
    // single row with a ×N badge instead of N duplicate rows. Each group keeps
    // its handle list (U8) so the row's controls can be scoped per instance.
    const groups = new Map<string, { id: string; channel: AudioChannel; handles: string[] }>();
    for (const e of entries) {
      const k = audioKey(e.id, e.channel);
      const g = groups.get(k);
      if (g) g.handles.push(e.handle);
      else groups.set(k, { id: e.id, channel: e.channel, handles: [e.handle] });
    }
    for (const [k, g] of groups) {
      const row = mkAudioRow(g.id, g.channel, g.handles);
      (playingBox ?? content).appendChild(row.row);
      audioRows.set(k, row);
    }
  }

  function audioKey(id: string, channel: string): string { return id + '|' + channel; }

  function mkAudioRow(id: string, channel: AudioChannel, handles: string[] = []): { row: HTMLDivElement; slider: HTMLInputElement } {
    const count = handles.length || 1;
    // U8 — partition this row's instances: engine-owned bed handles get no
    // stop/volume (the reconciler restarts + re-levels them per frame; their
    // level is the AUTHORED gain — edit it on the BEDS lane); everything else
    // (auditions, cue one-shots, music) keeps handle-scoped controls so a
    // same-asset audition's stop can't kill the live bed.
    const owned = opts.bedOwnedHandles?.() ?? new Set<string>();
    const free = handles.filter((h) => !owned.has(h));
    const allEngineBeds = handles.length > 0 && free.length === 0;
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      padding: '4px 6px',
      borderRadius: '4px',
      background: HOVER_BG,
      marginBottom: '4px',
    });
    const top = document.createElement('div');
    Object.assign(top.style, { display: 'flex', justifyContent: 'space-between', alignItems: 'center' });
    const name = document.createElement('span');
    name.textContent = id;
    name.style.maxWidth = '180px';
    name.style.whiteSpace = 'nowrap';
    name.style.overflow = 'hidden';
    name.style.textOverflow = 'ellipsis';
    const chip = document.createElement('span');
    // ×N badge when the same sound is playing from multiple sources (or briefly
    // doubled mid-crossfade) — so polyphony reads as one row, not duplicates.
    chip.textContent = count > 1 ? `${channel} ×${count}` : channel;
    chip.style.color = channelCssColor(channel);
    chip.style.fontSize = '9px';
    chip.style.letterSpacing = '0.5px';
    top.appendChild(name);
    top.appendChild(chip);
    const bottom = document.createElement('div');
    Object.assign(bottom.style, { display: 'flex', alignItems: 'center', gap: '6px' });
    const slider = document.createElement('input');
    if (allEngineBeds) {
      // No controls offered — the truthful affordance. Stopping would blip
      // (reconciler restarts next frame, re-rolled); the volume is recomputed
      // per frame from the authored gain × eraWeight × LFO.
      const note = document.createElement('span');
      note.textContent = 'bed · level authored on the BEDS lane';
      Object.assign(note.style, { color: MUTED, fontSize: '9px', fontStyle: 'italic' });
      bottom.appendChild(note);
    } else {
      slider.type = 'range';
      slider.min = '0';
      slider.max = '1';
      slider.step = '0.01';
      slider.value = String((free[0] !== undefined ? opts.audio.getHandleVolume(free[0]) : undefined) ?? opts.audio.getEntryVolume(id, channel) ?? 1);
      slider.style.flex = '1 1 auto';
      slider.addEventListener('input', () => {
        // Handle-scoped (U8): never touches engine-owned bed instances.
        for (const h of free) opts.audio.setHandleVolume(h, parseFloat(slider.value));
      });
      const stopBtn = document.createElement('button');
      stopBtn.textContent = '◼';
      Object.assign(stopBtn.style, {
        background: 'transparent', color: MUTED, border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: '3px', padding: '0 4px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '9px',
      });
      // Handle-scoped stop (U8): the old stop(id) was cross-channel, so
      // stopping a same-asset audition killed the engine's bed too.
      stopBtn.addEventListener('click', () => { for (const h of free) opts.audio.stopHandle(h, 0.1); });
      bottom.appendChild(slider);
      bottom.appendChild(stopBtn);
    }
    row.appendChild(top);
    row.appendChild(bottom);
    // Cross-surface focus: this live row IS the per-asset mixer — hovering it
    // lights the same sound on the timeline + in 3D; a focus elsewhere on the
    // asset (or its channel) lights this row back. _id/_ch feed the matcher.
    (row as HTMLDivElement & { _id?: string; _fch?: string })._id = id;
    (row as HTMLDivElement & { _id?: string; _fch?: string })._fch = channel;
    const acand: FocusRef = { assetId: id, channel };
    row.addEventListener('mouseenter', () => emitFocus(acand));
    row.addEventListener('mouseleave', () => emitFocus(null));
    return { row, slider };
  }

  // Inline gizmo-category toggle, mounted in the tab whose data it
  // visualizes. Co-locating each toggle with its data replaces the old flat
  // five-chip footer. Turning a category on while the master is off also
  // flips the master, so one click reveals the layer.
  function mkGizmoToggle(cat: GizmoCategory, label: string): HTMLDivElement {
    const el = document.createElement('div');
    Object.assign(el.style, {
      display: 'flex', alignItems: 'center', gap: '6px',
      cursor: 'pointer', fontSize: '10px', marginBottom: '8px', color: MUTED,
    });
    const dot = document.createElement('span');
    const txt = document.createElement('span');
    txt.textContent = label;
    el.appendChild(dot); el.appendChild(txt);
    el.addEventListener('click', () => {
      const next = !opts.dv.isCategoryEnabled(cat);
      opts.dv.setCategoryEnabled(cat, next);
      if (next && !opts.dv.enabled) opts.dv.setEnabled(true);
    });
    tabGizmoUnsubs.push(opts.dv.onChange((state) => {
      const on = state.enabled && state.categories[cat];
      dot.textContent = on ? '◉' : '○';
      el.style.color = on ? ACCENT : MUTED;
      el.style.opacity = state.enabled ? '1' : '0.6';
    }));
    return el;
  }

  function renderTab(): void {
    stopPerfLoop();
    // Drop the prior tab's gizmo-toggle listeners before rebuilding.
    for (const u of tabGizmoUnsubs) u();
    tabGizmoUnsubs.length = 0;
    if (currentTab === 'heroes') renderHeroes();
    else if (currentTab === 'audio') renderAudio();
    else if (currentTab === 'atmosphere') renderAtmosphere();
    else if (currentTab === 'camera') renderCamera();
    else if (currentTab === 'perf') { renderPerf(); startPerfLoop(); }
  }

  // ── render: atmosphere tab ─────────────────────────────────────────
  function renderAtmosphere(): void {
    content.innerHTML = '';
    content.appendChild(mkGizmoToggle('lighting', 'lighting gizmos (sun + AABBs)'));

    // Palette swatches.
    const paletteHead = mkSubhead('palette');
    content.appendChild(paletteHead);
    const palette = opts.sunRig.palette;
    const swatchRows: Array<[string, Color]> = [
      ['top', palette.top],
      ['horizon', palette.horizon],
      ['bottom', palette.bottom],
      ['cloud', palette.cloud],
      ['sun glow', palette.sunGlow],
      ['sun light', palette.sunLight],
      ['ambient', palette.ambient],
    ];
    for (const [label, color] of swatchRows) {
      content.appendChild(mkSwatchRow(label, color));
    }

    // Numeric palette readouts (sun light + ambient intensities).
    const intensities = document.createElement('div');
    Object.assign(intensities.style, { display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: MUTED, fontSize: '10px' });
    intensities.innerHTML =
      `<span>sun ×${palette.sunIntensity.toFixed(2)}</span>` +
      `<span>amb ×${palette.ambientIntensity.toFixed(2)}</span>` +
      `<span>alt ${opts.sunRig.altitude.toFixed(2)}</span>`;
    content.appendChild(intensities);

    // Preset selector.
    const presetHead = mkSubhead('preset');
    presetHead.style.marginTop = '10px';
    content.appendChild(presetHead);
    const presetWrap = document.createElement('div');
    Object.assign(presetWrap.style, { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' });
    const activeAtm = opts.getActiveAtmosphere();
    for (const name of Object.keys(opts.atmospheres)) {
      const btn = document.createElement('button');
      btn.textContent = name;
      const isActive = opts.atmospheres[name] === activeAtm;
      Object.assign(btn.style, {
        background: isActive ? 'rgba(102, 255, 230, 0.15)' : 'transparent',
        color: isActive ? ACCENT : TEXT,
        border: `1px solid ${isActive ? ACCENT : 'rgba(255,255,255,0.10)'}`,
        borderRadius: '3px',
        padding: '2px 6px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '10px',
      });
      btn.addEventListener('click', () => {
        opts.setAtmosphere(name);
        renderTab();
      });
      presetWrap.appendChild(btn);
    }
    content.appendChild(presetWrap);

    // Active atmosphere tunables.
    const t = opts.getActiveAtmosphere().getTunables?.();
    if (t && t.specs.length) {
      const tHead = mkSubhead('tunables');
      tHead.style.marginTop = '10px';
      content.appendChild(tHead);
      const tunablesTarget = t.target as Record<string, number>;
      for (const s of t.specs) {
        const slider = mkSliderRange(s.label, tunablesTarget[s.key], s.min, s.max, s.step, (v) => {
          tunablesTarget[s.key] = v;
        });
        content.appendChild(slider.row);
      }
    }

    // ── morning shaft editing (absorbed from the lil-gui atmosphere folder) ──
    const shaftHead = mkSubhead('morning shaft');
    shaftHead.style.marginTop = '12px';
    content.appendChild(shaftHead);
    const shaftHint = document.createElement('div');
    // DM10 — shaft handles are translate-only (the gizmo rejects rotate/scale on
    // them); say so visibly instead of implying W/E both work. When a handle is
    // active, surface the lock so the conductor knows why E/R do nothing.
    const shaftActive = !!activeShaftHandle && activeShaftHandle !== '(none)';
    shaftHint.textContent = shaftActive
      ? '🔒 translate-only — drag to move (rotate/scale don’t apply to handles)'
      : 'pick a handle, then drag to move it — translate-only (W)';
    Object.assign(shaftHint.style, { color: shaftActive ? ACCENT : MUTED, fontSize: '9px', marginBottom: '4px' });
    content.appendChild(shaftHint);
    const shaftWrap = document.createElement('div');
    Object.assign(shaftWrap.style, { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' });
    for (const id of opts.shaftHandleIds) {
      const chip = document.createElement('button');
      chip.textContent = id;
      const isActive = id === activeShaftHandle;
      Object.assign(chip.style, {
        background: isActive ? 'rgba(102, 255, 230, 0.15)' : 'transparent',
        color: isActive ? ACCENT : TEXT,
        border: `1px solid ${isActive ? ACCENT : 'rgba(255,255,255,0.10)'}`,
        borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '10px',
      });
      chip.addEventListener('click', () => { activeShaftHandle = id; opts.setShaftEditTarget(id); renderTab(); });
      shaftWrap.appendChild(chip);
    }
    content.appendChild(shaftWrap);
    const shaftActions = document.createElement('div');
    Object.assign(shaftActions.style, { display: 'flex', gap: '6px', flexWrap: 'wrap' });
    shaftActions.appendChild(mkButton('snap → shaft ref t', () => opts.snapToShaftRef()));
    const saveShaftBtn = mkButton('save → morning-shaft.json', () => {
      flashSaveResult(saveShaftBtn, 'save → morning-shaft.json', opts.saveShaftConfig());
    });
    shaftActions.appendChild(saveShaftBtn);
    content.appendChild(shaftActions);
  }

  function mkSwatchRow(label: string, color: Color): HTMLDivElement {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' });
    const sw = document.createElement('span');
    const hex = '#' + color.getHexString();
    Object.assign(sw.style, {
      width: '14px', height: '14px',
      borderRadius: '3px',
      background: hex,
      border: '1px solid rgba(255,255,255,0.10)',
      flexShrink: '0',
    });
    const lbl = document.createElement('span');
    lbl.textContent = label;
    lbl.style.color = MUTED;
    lbl.style.fontSize = '10px';
    lbl.style.width = '70px';
    const val = document.createElement('span');
    val.textContent = hex;
    val.style.color = TEXT;
    val.style.fontSize = '10px';
    val.style.marginLeft = 'auto';
    row.appendChild(sw);
    row.appendChild(lbl);
    row.appendChild(val);
    return row;
  }

  // ── render: camera tab (absorbed from the lil-gui camera folder) ────
  let cameraTabRenderedFor: CameraMode | null = null;
  function renderCamera(): void {
    content.innerHTML = '';
    cameraTabRenderedFor = opts.getActiveCamera();

    // Mode chips.
    content.appendChild(mkSubhead('mode'));
    const modeWrap = document.createElement('div');
    Object.assign(modeWrap.style, { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' });
    const activeCam = opts.getActiveCamera();
    for (const name of Object.keys(opts.cameras)) {
      const isActive = opts.cameras[name] === activeCam;
      const chip = document.createElement('button');
      chip.textContent = name;
      Object.assign(chip.style, {
        background: isActive ? 'rgba(102, 255, 230, 0.15)' : 'transparent',
        color: isActive ? ACCENT : TEXT,
        border: `1px solid ${isActive ? ACCENT : 'rgba(255,255,255,0.10)'}`,
        borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '10px',
      });
      chip.addEventListener('click', () => { opts.setCamera(name); renderTab(); });
      modeWrap.appendChild(chip);
    }
    content.appendChild(modeWrap);

    // Active-mode tunables (dynamic, mirrors the atmosphere-tab pattern).
    const tun = opts.getActiveCamera().getTunables();
    if (tun && tun.specs.length) {
      const tHead = mkSubhead('tunables'); tHead.style.marginTop = '10px'; content.appendChild(tHead);
      const target = tun.target as Record<string, number>;
      for (const s of tun.specs) {
        content.appendChild(mkSliderRange(s.label, target[s.key], s.min, s.max, s.step, (v) => { target[s.key] = v; }).row);
      }
    }

    // Bookmarks — clickable chips (go-to) with a per-chip ✕ delete, plus save…
    const bmHead = mkSubhead('bookmarks'); bmHead.style.marginTop = '10px'; content.appendChild(bmHead);
    const bmNames = Object.keys(opts.bookmarks).sort();
    if (bmNames.length === 0) {
      const empty = document.createElement('div'); empty.textContent = '(none saved)';
      Object.assign(empty.style, { color: MUTED, fontSize: '10px', marginBottom: '4px' });
      content.appendChild(empty);
    } else {
      const bmWrap = document.createElement('div');
      Object.assign(bmWrap.style, { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' });
      for (const name of bmNames) {
        const chip = document.createElement('div');
        Object.assign(chip.style, { display: 'flex', alignItems: 'center', gap: '5px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', padding: '2px 4px 2px 6px' });
        const bm = opts.bookmarks[name];
        // DM13 — the suffix shows the captured time of day (no era — bookmarks
        // transcend eras) and a ⌖ marker when the bookmark is hero-anchored.
        let moment = '';
        if (typeof bm.t === 'number') {
          const mins = ((Math.round(bm.t * 24 * 60) % 1440) + 1440) % 1440;
          const hm = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
          moment = ` · ${hm}`;
        }
        if (bm.anchor) moment += ` · ⌖${bm.anchor.replace(/^hero_/, '')}`;
        const go = document.createElement('span'); go.textContent = name + moment; go.title = `go to ${name}${moment}`;
        Object.assign(go.style, { cursor: 'pointer', fontSize: '10px', color: TEXT });
        go.addEventListener('click', () => { opts.goToBookmark(name); renderTab(); });
        const del = document.createElement('span'); del.textContent = '✕'; del.title = `delete ${name}`;
        Object.assign(del.style, { cursor: 'pointer', fontSize: '9px', color: MUTED });
        del.addEventListener('click', async () => {
          if (!window.confirm(`Delete bookmark "${name}"?`)) return;
          const ok = await opts.deleteBookmark(name); // await so we only drop the chip if it really left disk (S5)
          if (ok) renderTab();
          else { del.style.color = '#ff8a8a'; del.title = `delete failed — "${name}" still on disk`; }
        });
        chip.appendChild(go); chip.appendChild(del);
        bmWrap.appendChild(chip);
      }
      content.appendChild(bmWrap);
    }
    const bmSaveBtn = mkButton('save bookmark…', () => {
      const name = window.prompt('Bookmark name?'); if (!name) return;
      const trimmed = name.trim(); if (!trimmed) return;
      flashSaveResult(bmSaveBtn, 'save bookmark…', opts.saveCurrentAsBookmark(trimmed).then((ok) => {
        if (ok && currentTab === 'camera') renderTab(); // show the new chip only once it's actually on disk
        return ok;
      }));
    });
    content.appendChild(bmSaveBtn);

    // Wall cull.
    const cullHead = mkSubhead('wall cull'); cullHead.style.marginTop = '10px'; content.appendChild(cullHead);
    content.appendChild(mkCheckbox('enabled', opts.cullSettings.enabled, (v) => { opts.cullSettings.enabled = v; }).row);
    content.appendChild(mkSliderRange('offset (m)', opts.cullSettings.offset, -1, 3, 0.05, (v) => { opts.cullSettings.offset = v; }).row);

    // Poses.
    const poseHead = mkSubhead('poses'); poseHead.style.marginTop = '10px'; content.appendChild(poseHead);
    const poseRow = document.createElement('div');
    Object.assign(poseRow.style, { display: 'flex', gap: '6px', flexWrap: 'wrap' });
    const poseBtn = mkButton('set outside view', () => flashSaveResult(poseBtn, 'set outside view', opts.saveCameraPose()));
    const doorBtn = mkButton('set doorway', () => flashSaveResult(doorBtn, 'set doorway', opts.saveCurrentAsDoorway()));
    poseRow.appendChild(poseBtn);
    poseRow.appendChild(doorBtn);
    content.appendChild(poseRow);
  }

  // ── render: perf tab ───────────────────────────────────────────────
  // Frame time ring buffer — 120 samples, ~2 seconds at 60 fps.
  const FRAME_BUF = 120;
  const frameTimes = new Float32Array(FRAME_BUF);
  let frameIdx = 0;
  let frameCount = 0;
  let lastFrameAt = 0;

  // Refs to the chips we update inside the perf loop. Re-created each
  // time perf tab renders so they're stable.
  let perfFpsChip: HTMLSpanElement | null = null;
  let perfFrameChip: HTMLSpanElement | null = null;
  let perfCallsChip: HTMLSpanElement | null = null;
  let perfTrisChip: HTMLSpanElement | null = null;
  let perfGeosChip: HTMLSpanElement | null = null;
  let perfTexsChip: HTMLSpanElement | null = null;
  let perfAudioChip: HTMLSpanElement | null = null;
  let perfClothChip: HTMLSpanElement | null = null;
  let perfCanvas: HTMLCanvasElement | null = null;
  let perfRaf = 0;

  function renderPerf(): void {
    content.innerHTML = '';

    // Frame time chart.
    const chartHead = mkSubhead('frame time (last 2s)');
    content.appendChild(chartHead);
    perfCanvas = document.createElement('canvas');
    perfCanvas.width = 260;
    perfCanvas.height = 48;
    Object.assign(perfCanvas.style, {
      width: '100%',
      height: '48px',
      borderRadius: '3px',
      background: 'rgba(255,255,255,0.04)',
      marginBottom: '8px',
    });
    content.appendChild(perfCanvas);

    // Big numbers.
    const numbers = mkKVRow('fps', '–');
    perfFpsChip = numbers.val;
    content.appendChild(numbers.row);
    const frameRow = mkKVRow('frame ms', '–');
    perfFrameChip = frameRow.val;
    content.appendChild(frameRow.row);

    const rendererHead = mkSubhead('renderer');
    rendererHead.style.marginTop = '8px';
    content.appendChild(rendererHead);
    const callsRow = mkKVRow('draw calls', '–');
    perfCallsChip = callsRow.val;
    content.appendChild(callsRow.row);
    const trisRow = mkKVRow('triangles', '–');
    perfTrisChip = trisRow.val;
    content.appendChild(trisRow.row);
    const geosRow = mkKVRow('geometries', '–');
    perfGeosChip = geosRow.val;
    content.appendChild(geosRow.row);
    const texsRow = mkKVRow('textures', '–');
    perfTexsChip = texsRow.val;
    content.appendChild(texsRow.row);

    const subsysHead = mkSubhead('subsystems');
    subsysHead.style.marginTop = '8px';
    content.appendChild(subsysHead);
    const audioRow = mkKVRow('audio sources', '–');
    perfAudioChip = audioRow.val;
    content.appendChild(audioRow.row);
    const clothRow = mkKVRow('cloth particles', '–');
    perfClothChip = clothRow.val;
    content.appendChild(clothRow.row);

    // Scene-level gizmo toggles that have no dedicated tab of their own.
    const gizHead = mkSubhead('scene gizmos');
    gizHead.style.marginTop = '10px';
    content.appendChild(gizHead);
    content.appendChild(mkGizmoToggle('cloth', 'cloth wireframe'));
    content.appendChild(mkGizmoToggle('culling', 'culling (walls + interior)'));
    content.appendChild(mkGizmoToggle('cameras', 'camera markers'));
  }

  function startPerfLoop(): void {
    if (perfRaf) return;
    lastFrameAt = performance.now();
    const tick = (now: number): void => {
      const dt = now - lastFrameAt;
      lastFrameAt = now;
      frameTimes[frameIdx] = dt;
      frameIdx = (frameIdx + 1) % FRAME_BUF;
      frameCount = Math.min(FRAME_BUF, frameCount + 1);

      // Mean frame time over the buffer (skip first ~10 frames so the
      // chart isn't biased by the initial tab-switch hitch).
      let sum = 0;
      for (let i = 0; i < frameCount; i++) sum += frameTimes[i];
      const meanMs = frameCount > 0 ? sum / frameCount : 0;
      const fps = meanMs > 0 ? Math.round(1000 / meanMs) : 0;
      if (perfFpsChip) perfFpsChip.textContent = `${fps}`;
      if (perfFrameChip) perfFrameChip.textContent = `${meanMs.toFixed(1)}`;
      // Color the FPS chip by budget. Green-ish under 18ms, amber under
      // 33ms, red above. We use ACCENT cyan as the "good" tone.
      if (perfFpsChip) perfFpsChip.style.color = meanMs < 18 ? ACCENT : meanMs < 33 ? '#ffd87c' : '#ff7eb6';

      // Renderer stats.
      const info = opts.renderer.info;
      if (perfCallsChip) perfCallsChip.textContent = `${info.render.calls}`;
      if (perfTrisChip) perfTrisChip.textContent = `${info.render.triangles.toLocaleString()}`;
      if (perfGeosChip) perfGeosChip.textContent = `${info.memory.geometries}`;
      if (perfTexsChip) perfTexsChip.textContent = `${info.memory.textures}`;

      // Subsystems.
      if (perfAudioChip) perfAudioChip.textContent = `${opts.audio.listPlaying().length}`;
      let particles = 0;
      for (const c of opts.cloths) particles += c.particleCount;
      if (perfClothChip) perfClothChip.textContent = `${particles}`;

      // Sparkline.
      drawFramePlot();

      perfRaf = requestAnimationFrame(tick);
    };
    perfRaf = requestAnimationFrame(tick);
  }

  function stopPerfLoop(): void {
    if (perfRaf) {
      cancelAnimationFrame(perfRaf);
      perfRaf = 0;
    }
  }

  function drawFramePlot(): void {
    if (!perfCanvas) return;
    const ctx = perfCanvas.getContext('2d');
    if (!ctx) return;
    const w = perfCanvas.width;
    const h = perfCanvas.height;
    ctx.clearRect(0, 0, w, h);
    // Two threshold lines: 16.6ms (60fps) and 33ms (30fps).
    const yFor = (ms: number) => h - Math.min(h, Math.max(0, ms / 50 * h));
    ctx.strokeStyle = 'rgba(102, 255, 230, 0.18)';
    ctx.beginPath();
    ctx.moveTo(0, yFor(16.6));
    ctx.lineTo(w, yFor(16.6));
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255, 216, 124, 0.18)';
    ctx.beginPath();
    ctx.moveTo(0, yFor(33));
    ctx.lineTo(w, yFor(33));
    ctx.stroke();
    // Plot the ring buffer left-to-right, oldest first. The newest
    // sample is at index (frameIdx-1+FRAME_BUF)%FRAME_BUF.
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < frameCount; i++) {
      // Walk from oldest to newest. The oldest is at frameIdx if buffer
      // is full, or 0 if not yet wrapped.
      const start = frameCount < FRAME_BUF ? 0 : frameIdx;
      const j = (start + i) % FRAME_BUF;
      const x = (i / Math.max(1, frameCount - 1)) * w;
      const y = yFor(frameTimes[j]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  function mkKVRow(key: string, valTxt: string): { row: HTMLDivElement; val: HTMLSpanElement } {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '10px' });
    const k = document.createElement('span');
    k.textContent = key;
    k.style.color = MUTED;
    const v = document.createElement('span');
    v.textContent = valTxt;
    v.style.color = TEXT;
    v.style.fontFamily = 'inherit';
    row.appendChild(k);
    row.appendChild(v);
    return { row, val: v };
  }

  function mkSliderRange(label: string, initial: number, min: number, max: number, step: number, onChange: (v: number) => void): { row: HTMLDivElement } {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' });
    const lbl = document.createElement('span');
    lbl.textContent = label;
    lbl.style.width = '80px';
    lbl.style.color = MUTED;
    lbl.style.fontSize = '10px';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(initial);
    slider.style.flex = '1 1 auto';
    const val = document.createElement('span');
    val.textContent = formatNum(initial);
    val.style.color = MUTED;
    val.style.fontSize = '9px';
    val.style.minWidth = '36px';
    val.style.textAlign = 'right';
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      onChange(v);
      val.textContent = formatNum(v);
    });
    row.appendChild(lbl);
    row.appendChild(slider);
    row.appendChild(val);
    return { row };
  }

  function formatNum(v: number): string {
    if (Math.abs(v) >= 100) return v.toFixed(0);
    if (Math.abs(v) >= 1) return v.toFixed(2);
    return v.toFixed(3);
  }

  // ── audio event subscriptions ──────────────────────────────────────
  const onAudioEvent = (): void => {
    if (currentTab !== 'audio') return;
    renderPlayingList();
    refreshAssets();
    applyInspectorFocus(); // re-light freshly rebuilt PLAYING-NOW rows
  };
  const unsubPlay = devBus.on('audio:play', onAudioEvent);
  const unsubStop = devBus.on('audio:stop', onAudioEvent);
  // Paint the cross-surface focus glow onto every audio widget that matches —
  // palette chips (by asset), PLAYING-NOW rows (by asset/channel), channel
  // faders (by channel) — in the sound's channel color. Hero rows are handled
  // by applyHeroRowStates via hoveredHeroId below.
  const applyInspectorFocus = (): void => {
    for (const [id, chip] of assetChips) {
      const ch = (chip as HTMLSpanElement & { _fch?: string })._fch ?? 'sfx';
      applyFocusGlow(chip, focusMatches(currentFocus, { assetId: id }), ch);
    }
    for (const { row } of audioRows.values()) {
      const r = row as HTMLDivElement & { _id?: string; _fch?: string };
      if (!r._id) continue; // skip the "(nothing playing)" placeholder
      applyFocusGlow(row, focusMatches(currentFocus, { assetId: r._id, channel: r._fch }), r._fch ?? '');
    }
    for (const [ch, row] of channelFaderRows) {
      applyFocusGlow(row, focusMatches(currentFocus, { channel: ch }), ch);
    }
  };
  // Unified selection — echo the active/hovered hero onto the hero rows.
  const unsubSelect = devBus.on('hero:select', ({ id }) => {
    selectedHeroId = id;
    applyHeroRowStates();
  });
  // Unified focus — a hover on ANY audio surface (3D hero, timeline lane, our
  // own widgets) arrives here. Echo the anchored hero onto the hero rows and
  // light every matching audio widget. Replaces the old hero:hover listener:
  // 3D hero hovers now reach us re-broadcast as audio:focus by app.ts.
  const unsubFocus = devBus.on('audio:focus', ({ ref }) => {
    currentFocus = ref;
    hoveredHeroId = ref?.heroId ?? null;
    applyHeroRowStates();
    applyInspectorFocus();
  });
  // Reflect the armed cue-brush in the palette — armed here or from the
  // timeline's picker, it's the same bus event either way.
  const unsubArm = devBus.on('audio:arm', ({ id }) => {
    armedAssetId = id;
    refreshArmed();
  });
  // Live poll for the palette's armed/playing visuals (auditions started
  // elsewhere). Only does work on the AUDIO tab; cheap no-op otherwise.
  const audioPoll = window.setInterval(() => {
    if (currentTab !== 'audio') return;
    refreshAssets();
    // Mirror master + mute here if they were changed via the chrome control.
    outMaster?.set(opts.audio.getMasterVolume());
    outMuted?.set(opts.audio.muted);
  }, 200);
  // Keep the HEROES-tab gizmo-mode toggle in sync with the W/E keys.
  const gizmoModePoll = window.setInterval(() => {
    if (currentTab === 'heroes') syncGizmoMode();
  }, 200);
  // Re-render the CAMERA tab if the active camera changed elsewhere (selecting
  // a hero switches to freeform; a bookmark go-to lands in freeform).
  const cameraPoll = window.setInterval(() => {
    if (currentTab === 'camera' && opts.getActiveCamera() !== cameraTabRenderedFor) renderTab();
  }, 200);

  // ── dev mode hide ──────────────────────────────────────────────────
  const unsubDev = onDevModeChange((on) => {
    root.style.display = on ? 'flex' : 'none';
  });

  // ── init ───────────────────────────────────────────────────────────
  setCollapsed(collapsed);
  setTab(currentTab);
  document.body.appendChild(root);

  return {
    root,
    refreshHeroes,
    dispose(): void {
      unsubPlay();
      unsubStop();
      unsubSelect();
      unsubFocus();
      unsubArm();
      unsubDev();
      unsubGizmo();
      unregHotkey();
      window.removeEventListener('keydown', onGizmoKey);
      window.clearInterval(audioPoll);
      window.clearInterval(gizmoModePoll);
      window.clearInterval(cameraPoll);
      stopPerfLoop();
      for (const u of tabGizmoUnsubs) u();
      root.remove();
    },
  };
}

// ── helpers ───────────────────────────────────────────────────────────

function mkSubhead(label: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = label;
  Object.assign(d.style, {
    color: SUBHEAD,
    fontSize: '9px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '4px',
  });
  return d;
}

// Small action button — the inspector's standard "do a thing / save" control.
function mkButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  Object.assign(b.style, {
    padding: '4px 8px', background: 'rgba(255,255,255,0.06)', color: TEXT,
    border: '1px solid rgba(255,255,255,0.18)', borderRadius: '4px',
    cursor: 'pointer', font: 'inherit', fontSize: '10px',
  });
  b.addEventListener('click', onClick);
  return b;
}

// Await a save and reflect the REAL outcome on a button (S5): 'saving…' in
// flight, then 'saved ✓' green / 'save ✕' red, restoring `label` after a beat.
// Replaces the optimistic flash that showed ✓ even when the dev server was down.
// DM5 — one save model: render the shared SaveVis vocabulary (the same the
// timeline transport uses) and HOLD a disk conflict on screen (it needs the
// user to reload-or-force), while saved/failed restore the label after a beat.
// Accepts a SaveResult (conflict-aware) or a plain boolean (flows not yet
// conflict-aware map true→saved / false→failed).
function flashSaveResult(btn: HTMLButtonElement, label: string, p: Promise<SaveResult | boolean>): void {
  btn.disabled = true;
  paintSave(btn, 'saving', label);
  p.then(
    (r): SaveVis => (r === true || r === 'saved') ? 'saved' : (r === 'conflict' ? 'conflict' : 'failed'),
    (): SaveVis => 'failed',
  ).then((vis) => {
    paintSave(btn, vis, label);
    btn.disabled = false;
    // Conflict is persistent (reload-or-force); saved/failed are transient.
    if (vis !== 'conflict') {
      setTimeout(() => { btn.textContent = label; btn.style.color = TEXT; btn.style.borderColor = 'rgba(255,255,255,0.12)'; }, 1600);
    }
  });
}

function mkSlider(label: string, initial: number, onChange: (v: number) => void): { row: HTMLDivElement; set: (v: number) => void } {
  const row = document.createElement('div');
  Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' });
  const lbl = document.createElement('span');
  lbl.textContent = label;
  lbl.style.width = '70px';
  lbl.style.color = MUTED;
  lbl.style.fontSize = '10px';
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '1';
  slider.step = '0.01';
  slider.value = String(initial);
  slider.style.flex = '1 1 auto';
  const val = document.createElement('span');
  val.textContent = initial.toFixed(2);
  val.style.color = MUTED;
  val.style.fontSize = '9px';
  val.style.minWidth = '28px';
  val.style.textAlign = 'right';
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    onChange(v);
    val.textContent = v.toFixed(2);
  });
  row.appendChild(lbl);
  row.appendChild(slider);
  row.appendChild(val);
  // External sync (e.g. the chrome mute changing master) — skip while the user
  // is dragging this very slider so we don't fight their input.
  const set = (v: number): void => {
    if (document.activeElement === slider) return;
    slider.value = String(v);
    val.textContent = v.toFixed(2);
  };
  return { row, set };
}

function mkCheckbox(label: string, initial: boolean, onChange: (v: boolean) => void): { row: HTMLDivElement; set: (v: boolean) => void } {
  const row = document.createElement('div');
  Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', cursor: 'pointer' });
  const lbl = document.createElement('span');
  lbl.textContent = label;
  lbl.style.width = '70px';
  lbl.style.color = MUTED;
  lbl.style.fontSize = '10px';
  const dot = document.createElement('span');
  dot.textContent = initial ? '●' : '○';
  dot.style.color = initial ? ACCENT : MUTED;
  dot.style.fontSize = '11px';
  row.appendChild(lbl);
  row.appendChild(dot);
  const paint = (v: boolean): void => { dot.textContent = v ? '●' : '○'; dot.style.color = v ? ACCENT : MUTED; };
  row.addEventListener('click', () => {
    const v = dot.textContent === '○';
    onChange(v);
    paint(v);
  });
  return { row, set: paint };
}

// Sound-palette grouping: cluster assets by their source family so the AUDIO
// tab reads as a library, not a flat list.
const PALETTE_GROUPS: Array<{ name: string; match: (id: string) => boolean }> = [
  { name: 'Fan', match: (id) => id.startsWith('sfx_fan') },
  { name: 'Sewing', match: (id) => id.startsWith('sfx_sewing') },
  { name: 'Generator', match: (id) => id.startsWith('sfx_gen') },
  { name: 'Motorcycle', match: (id) => id.startsWith('sfx_moto') },
  { name: 'Music', match: (id) => id.startsWith('test_music') },
];
// Short label within a group: drop the family prefix, spaces for underscores.
function stripPrefix(id: string): string {
  return id
    .replace(/^sfx_(fan|sewing|gen|moto)_/, '')
    .replace(/^sfx_/, '')
    .replace(/^test_music_/, '')
    .replace(/_/g, ' ');
}
